import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatErr } from "../lib/api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // If returning from Emergent OAuth callback, skip /me until AuthCallback runs.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data.user);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatErr(e) };
    }
  };

  const register = async (email, password, name, birth_date = null, picture = null, attribution = null) => {
    try {
      const payload = { email, password, name };
      if (birth_date) payload.birth_date = birth_date;
      if (picture) payload.picture = picture;
      if (attribution?.source) payload.signup_source = attribution.source;
      if (attribution?.referrer) payload.signup_referrer = attribution.referrer;
      const { data } = await api.post("/auth/register", payload);
      setUser(data.user);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatErr(e) };
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    setUser(null);
  };

  const exchangeEmergentSession = async (sessionId) => {
    const { data } = await api.post("/auth/session", { session_id: sessionId });
    setUser(data.user);
    return { user: data.user, isNew: !!data.is_new_user };
  };

  return (
    <AuthCtx.Provider value={{ user, loading, login, register, logout, checkAuth, refreshUser: checkAuth, exchangeEmergentSession, setUser }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
